import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  Business as BusinessIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Lightbulb as LightbulbIcon,
  Quiz as QuizIcon,
  Psychology as PsychologyIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { ResponsiveRadar } from '@nivo/radar';
import { useLocation, useNavigate } from 'react-router-dom';
import { TopBar } from '../../components/top_bar/TopBar';
import { Helmet } from 'react-helmet';
import { useUser } from '@clerk/clerk-react';
import axios from 'axios';
import posthog from 'posthog-js';
import './QuestionBankFeedback.css';

const QuestionBankFeedback = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [similarQuestions, setSimilarQuestions] = useState([]);
    
    const location = useLocation();
    const navigate = useNavigate();
    const { user, isSignedIn } = useUser();

    const TITLE = 'Question Practice Feedback - Mirwork';

    useEffect(() => {
        posthog.capture('$pageview', {
            $current_url: window.location.href,
            page_type: 'question_bank_feedback'
        });
        
        if (isSignedIn) {
            posthog.identify(user.primaryEmailAddress.emailAddress);
            fetchFeedback();
        }
    }, [isSignedIn]);

    const fetchFeedback = async () => {
        try {
            const urlParams = new URLSearchParams(location.search);
            const sessionId = urlParams.get('sessionId');

            if (!sessionId) {
                setError('Missing session ID');
                setLoading(false);
                return;
            }

            // Try to get saved feedback first
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/questionBank/feedback/${sessionId}`,
                {
                    params: {
                        userEmail: user.primaryEmailAddress.emailAddress
                    }
                }
            );

            setFeedback(response.data.data);
            setSimilarQuestions(response.data.data.similarQuestions || []);
            setLoading(false);
        } catch (err) {
            console.error('Failed to fetch feedback:', err);
            setError('Failed to load feedback. Please try again.');
            setLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        posthog.capture('qb_feedback_tab_change', { tab: newValue });
    };

    const handlePracticeSimilar = (questionId) => {
        posthog.capture('qb_feedback_practice_similar', { questionId });
        navigate(`/interview?mode=practice&questionId=${questionId}`);
    };

    const handlePracticeFollowup = () => {
        posthog.capture('qb_feedback_practice_followup');
        // Navigate to follow-up practice mode
        navigate(`/interview?mode=followup&originalQuestionId=${feedback.question._id}`);
    };

    const getScoreColor = (score) => {
        if (score >= 4) return '#4caf50';
        if (score >= 3) return '#ff9800';
        return '#f44336';
    };

    const getPerformanceIcon = (score) => {
        if (score >= 4) return <CheckIcon sx={{ color: '#4caf50' }} />;
        if (score >= 3) return <WarningIcon sx={{ color: '#ff9800' }} />;
        return <ErrorIcon sx={{ color: '#f44336' }} />;
    };

    const radarData = feedback ? feedback.analysis.framework.map(item => ({
        criteria: item.section.length > 12 ? item.section.substring(0, 12) + '...' : item.section,
        score: typeof item.score === 'number' ? item.score : 0
    })) : [];

    const radarOptions = {
        maxValue: 5,
        margin: { top: 40, right: 80, bottom: 40, left: 80 },
        borderWidth: 2,
        borderColor: { from: 'color' },
        gridLevels: 5,
        gridShape: 'linear',
        gridLabelOffset: 36,
        enableDots: true,
        dotSize: 8,
        dotColor: { theme: 'background' },
        dotBorderWidth: 2,
        dotBorderColor: { from: 'color' },
        enableDotLabel: false,
        colors: { scheme: 'category10' },
        fillOpacity: 0.25,
        blendMode: 'multiply',
        animate: true,
        motionConfig: 'wobbly'
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container>
                <TopBar />
                <Alert severity="error" sx={{ mt: 4 }}>
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>{TITLE}</title>
            </Helmet>
            <TopBar />
            <Container maxWidth="lg" className="qb-feedback-container">
                {/* Header Section */}
                <Paper className="qb-feedback-header" elevation={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <BusinessIcon sx={{ color: '#1976d2' }} />
                        <Typography variant="h6" component="span">
                            {feedback.question.company}
                        </Typography>
                        <Chip 
                            label={feedback.question.standardizedQuestionType} 
                            size="small" 
                            color="primary" 
                        />
                        <Chip 
                            label={feedback.question.difficulty} 
                            size="small" 
                            color={feedback.question.difficulty === 'Hard' ? 'error' : 
                                   feedback.question.difficulty === 'Medium' ? 'warning' : 'success'} 
                        />
                        <Chip 
                            label={`${feedback.question.timesAsked || 0} practices`} 
                            size="small" 
                            variant="outlined" 
                        />
                    </Box>
                    
                    <Typography variant="h4" className="qb-feedback-question-title">
                        {feedback.question.interviewQuestion}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Practice Time: {feedback.practiceTime || 'Not recorded'}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            Words: {feedback.wordCount || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Completed: {new Date(feedback.completedAt).toLocaleDateString()}
                        </Typography>
                    </Box>
                </Paper>

                {/* Score Overview Section */}
                <Grid container spacing={3} className="qb-feedback-score-section">
                    <Grid item xs={12} md={3}>
                        <Card className="qb-score-card">
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" color="text.secondary">Your Score</Typography>
                                <Typography 
                                    variant="h2" 
                                    sx={{ color: getScoreColor(feedback.analysis.overallScore), fontWeight: 'bold' }}
                                >
                                    {feedback.analysis.overallScore.toFixed(1)}
                                </Typography>
                                <Typography variant="h6" color="text.secondary">/5</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                        <Card className="qb-score-card">
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" color="text.secondary">Industry Standard</Typography>
                                <Typography variant="h2" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                                    {feedback.analysis.industryStandard.toFixed(1)}
                                </Typography>
                                <Typography variant="h6" color="text.secondary">/5</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                                    <TrendingIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                    <Typography variant="body2">
                                        Top {feedback.analysis.percentile}%
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card className="qb-radar-card">
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2 }}>Performance Breakdown</Typography>
                                <Box sx={{ height: 200 }}>
                                    <ResponsiveRadar
                                        data={radarData}
                                        keys={['score']}
                                        indexBy="criteria"
                                        {...radarOptions}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Analysis Tabs */}
                <Paper className="qb-feedback-analysis" elevation={2}>
                    <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab icon={<AssignmentIcon />} label="Framework Analysis" />
                        <Tab icon={<LightbulbIcon />} label="Industry Context" />
                        <Tab icon={<QuizIcon />} label="Follow-up Questions" />
                        <Tab icon={<PsychologyIcon />} label="Similar Questions" />
                    </Tabs>

                    <Box sx={{ p: 3 }}>
                        {/* Framework Analysis Tab */}
                        {activeTab === 0 && (
                            <Box>
                                <Typography variant="h6" sx={{ mb: 3 }}>
                                    Optimal Answer Structure Analysis
                                </Typography>
                                {feedback.analysis.framework.map((section, index) => (
                                    <Card key={index} sx={{ mb: 2 }} elevation={1}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                {getPerformanceIcon(section.score)}
                                                <Typography variant="h6" sx={{ ml: 1, flex: 1 }}>
                                                    {section.section}
                                                </Typography>
                                                <Typography 
                                                    variant="h6" 
                                                    sx={{ color: getScoreColor(section.score) }}
                                                >
                                                    {section.score}/5
                                                </Typography>
                                            </Box>
                                            <LinearProgress 
                                                variant="determinate" 
                                                value={(section.score / 5) * 100}
                                                sx={{ 
                                                    mb: 2, 
                                                    '& .MuiLinearProgress-bar': {
                                                        backgroundColor: getScoreColor(section.score)
                                                    }
                                                }}
                                            />
                                            <Typography variant="body1" paragraph>
                                                {section.feedback}
                                            </Typography>
                                            
                                            {section.reasoning && (
                                                <Box sx={{ p: 2, bgcolor: 'info.light', color: 'info.contrastText', borderRadius: 1, mb: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                        🎯 Why This Matters:
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {section.reasoning}
                                                    </Typography>
                                                </Box>
                                            )}
                                            
                                            {section.improvement && (
                                                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                        💡 How to Improve:
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {section.improvement}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        )}

                        {/* Industry Context Tab */}
                        {activeTab === 1 && (
                            <Box>
                                <Typography variant="h6" sx={{ mb: 3 }}>
                                    Real-World Context & Industry Insights
                                </Typography>
                                
                                {feedback.industryContext.actualImplementation && (
                                    <Card sx={{ mb: 3 }} elevation={1}>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ mb: 2 }}>
                                                📰 Actual Implementation
                                            </Typography>
                                            <Typography variant="body1" paragraph>
                                                {feedback.industryContext.actualImplementation}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                )}

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Card elevation={1}>
                                            <CardContent>
                                                <Typography variant="h6" sx={{ mb: 2 }}>
                                                    🎯 Key Focus Areas
                                                </Typography>
                                                <List dense>
                                                    {feedback.industryContext.keyFocusAreas.map((area, index) => (
                                                        <ListItem key={index}>
                                                            <ListItemIcon>
                                                                <CheckIcon sx={{ fontSize: 16 }} />
                                                            </ListItemIcon>
                                                            <ListItemText primary={area} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    
                                    <Grid item xs={12} md={6}>
                                        <Card elevation={1}>
                                            <CardContent>
                                                <Typography variant="h6" sx={{ mb: 2 }}>
                                                    📊 Success Metrics
                                                </Typography>
                                                <List dense>
                                                    {feedback.industryContext.successMetrics.map((metric, index) => (
                                                        <ListItem key={index}>
                                                            <ListItemIcon>
                                                                <TrendingIcon sx={{ fontSize: 16 }} />
                                                            </ListItemIcon>
                                                            <ListItemText primary={metric} />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    
                                    {feedback.industryContext.commonMistakes && feedback.industryContext.commonMistakes.length > 0 && (
                                        <Grid item xs={12}>
                                            <Card elevation={1} sx={{ bgcolor: 'warning.light' }}>
                                                <CardContent>
                                                    <Typography variant="h6" sx={{ mb: 2 }}>
                                                        ⚠️ Common Mistakes to Avoid
                                                    </Typography>
                                                    <List dense>
                                                        {feedback.industryContext.commonMistakes.map((mistake, index) => (
                                                            <ListItem key={index}>
                                                                <ListItemIcon>
                                                                    <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                                                </ListItemIcon>
                                                                <ListItemText primary={mistake} />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        )}

                        {/* Follow-up Questions Tab */}
                        {activeTab === 2 && (
                            <Box>
                                <Typography variant="h6" sx={{ mb: 3 }}>
                                    Likely Interviewer Follow-up Questions
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Based on your answer, here's what they'd ask next:
                                </Typography>
                                
                                {feedback.followUpQuestions.map((followUp, index) => (
                                    <Card key={index} sx={{ mb: 2 }} elevation={1}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <Typography variant="h6" sx={{ flex: 1 }}>
                                                    {index + 1}. "{followUp.question}"
                                                </Typography>
                                                <Chip 
                                                    label={followUp.difficulty || 'intermediate'} 
                                                    size="small"
                                                    color={
                                                        followUp.difficulty === 'advanced' ? 'error' : 
                                                        followUp.difficulty === 'basic' ? 'success' : 'warning'
                                                    }
                                                />
                                            </Box>
                                            <Box sx={{ p: 2, bgcolor: 'info.main', color: 'info.contrastText', borderRadius: 1 }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                    💡 Hint:
                                                </Typography>
                                                <Typography variant="body2">
                                                    {followUp.hint}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                                
                                {/* TODO: Implement Practice Follow-ups feature */}
                                {/* <Box sx={{ textAlign: 'center', mt: 3 }}>
                                    <Button 
                                        variant="contained" 
                                        size="large"
                                        onClick={handlePracticeFollowup}
                                        sx={{ minWidth: 200 }}
                                    >
                                        Practice Follow-ups
                                    </Button>
                                </Box> */}
                            </Box>
                        )}

                        {/* Similar Questions Tab */}
                        {activeTab === 3 && (
                            <Box>
                                <Typography variant="h6" sx={{ mb: 3 }}>
                                    Similar Questions to Practice
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Build your skills with these related questions:
                                </Typography>
                                
                                <Grid container spacing={2}>
                                    {similarQuestions.map((question, index) => (
                                        <Grid item xs={12} md={6} key={index}>
                                            <Card 
                                                sx={{ 
                                                    cursor: 'pointer',
                                                    transition: 'transform 0.2s',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: 3
                                                    }
                                                }}
                                                onClick={() => handlePracticeSimilar(question._id)}
                                            >
                                                <CardContent>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                        <BusinessIcon sx={{ mr: 1, fontSize: 16 }} />
                                                        <Typography variant="subtitle2">
                                                            {question.company}
                                                        </Typography>
                                                        <Box sx={{ flex: 1 }} />
                                                        <Chip 
                                                            label={question.difficulty} 
                                                            size="small"
                                                            color={question.difficulty === 'Hard' ? 'error' : 
                                                                   question.difficulty === 'Medium' ? 'warning' : 'success'}
                                                        />
                                                    </Box>
                                                    
                                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                                        {question.interviewQuestion}
                                                    </Typography>
                                                    
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Chip 
                                                            label={question.standardizedQuestionType} 
                                                            size="small" 
                                                            variant="outlined" 
                                                        />
                                                        <Typography variant="body2" color="text.secondary">
                                                            {question.timesAsked || 0} practices
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}
                    </Box>
                </Paper>

                {/* Action Buttons */}
                <Box className="qb-feedback-actions">
                    {/* TODO: Implement Practice Follow-ups feature */}
                    {/* <Button 
                        variant="contained" 
                        size="large"
                        onClick={handlePracticeFollowup}
                        sx={{ mr: 2 }}
                    >
                        Practice Follow-ups
                    </Button> */}
                    <Button 
                        variant="outlined" 
                        size="large"
                        onClick={() => navigate('/enhanced-question-bank')}
                        sx={{ mr: 2 }}
                    >
                        Back to Question Bank
                    </Button>
                    <Button 
                        variant="outlined" 
                        size="large"
                        onClick={() => window.open('/enhanced-question-bank', '_blank')}
                    >
                        Try Another Question
                    </Button>
                </Box>
            </Container>
        </>
    );
};

export default QuestionBankFeedback;